import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { SocketProvider } from './contexts/SocketContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { ForwardingProvider } from './contexts/ForwardingContext'
import { GroupUnreadProvider } from './contexts/GroupUnreadContext'
import { WhatsAppStatusProvider } from './contexts/WhatsAppStatusContext'
import { ProtectedRoute } from './components/shared/ProtectedRoute'
import { Layout } from './components/shared/Layout'
import { LoginPage } from './components/LoginPage'
import { ProfilePage } from './components/profile/ProfilePage'
import { QueueView } from './components/queue/QueueView'
import { ChatWorkspace } from './components/chat/ChatWorkspace'
import { VisitorsPage } from './components/visitors/VisitorsPage'
import { MyChatsView } from './components/queue/MyChatsView'
import { MissedChatsView } from './components/queue/MissedChatsView'
import { AppModeProvider } from './contexts/AppModeContext'
import { DevMode } from './components/dev/DevMode'
import { PlatformUpdatesPage } from './components/updates/PlatformUpdatesPage'
import { CobrowseStandalonePage } from './components/cobrowse/CobrowseStandalonePage'
import { AgentDetail } from './components/chat-history/AgentDetail'
import { ChatHistoryCompanyDetail } from './components/chat-history/ChatHistoryCompanyDetail'
import { GroupsView } from './components/groups/GroupsView'
import { ContactsView } from './components/contacts/ContactsView'
import { PaidCustomersView } from './components/paid-customers/PaidCustomersView'
import { CompanyDetailView } from './components/paid-customers/CompanyDetailView'
import { AiCopilotView } from './components/ai-copilot/AiCopilotView'
import { TicketsInbox } from './components/tickets/TicketsInbox'
import { ResolutionNotesView } from './components/notes/ResolutionNotesView'
import { ChatsShell } from './components/queue/ChatsShell'
import { CustomersShell } from './components/paid-customers/CustomersShell'
import { DashboardHome } from './components/dashboard/DashboardHome'
import { SettingsHub } from './components/settings/SettingsHub'
import { CrmRecordList } from './components/crm/CrmRecordList'
import { CrmReportBuilder } from './components/crm/CrmReportBuilder'
import { CrmEmailTemplates } from './components/crm/CrmEmailTemplates'
// Anti-Churn Dash replaced Customer Success (Lee, 23 Oct 2026); old page kept on disk, unrouted.
import { CrmAntiChurn } from './components/crm/CrmAntiChurn'
import { CrmObTodo } from './components/crm/CrmObTodo'
import { CrmObFollowups } from './components/crm/CrmObFollowups'
import { CrmObCalendar } from './components/crm/CrmObCalendar'
import { CrmObAnalytics } from './components/crm/CrmObAnalytics'
import { CrmObCompanyPage } from './components/crm/CrmObCompanyPage'
import { CrmOnboardings } from './components/crm/CrmOnboardings'
import { OnboardingEmbedPage } from './components/crm/OnboardingEmbedPage'
import { CrmOnboardingPage } from './components/crm/CrmOnboardingPage'
import { CrmContracts } from './components/crm/CrmContracts'
import { ContractStudio } from './components/crm/ContractStudio'
import { ContractPublicPage } from './components/crm/ContractPublicPage'
import { CrmShowDetail } from './components/crm/CrmShowDetail'
import { CrmLeadPage } from './components/crm/CrmLeadPage'
import { CrmLeadImport } from './components/crm/CrmLeadImport'
import { CrmBoardHub } from './components/crm/CrmBoardHub'
import { CrmMyDay } from './components/crm/CrmMyDay'
import { CrmLeadsHub } from './components/crm/CrmLeadsHub'
import { CrmShowsHub } from './components/crm/CrmShowsHub'
// CSS is imported in main.tsx

const QueueWithChat: React.FC = () => {
  const { chatId } = useParams<{ chatId?: string }>()
  if (chatId) {
    return <ChatWorkspace chatId={chatId} list={<QueueView />} />
  }
  return <QueueView />
}

const MyChatsWithChat: React.FC = () => {
  const { chatId } = useParams<{ chatId?: string }>()
  if (chatId) {
    return <ChatWorkspace chatId={chatId} list={<MyChatsView />} />
  }
  return <MyChatsView />
}

/** Missed / Tickets / Resolution tabs read the shared "My chats only" toggle
 *  (?mine=1) and pass it to the view as a filter. */
const MissedRoute: React.FC = () => {
  const [sp] = useSearchParams()
  return <MissedChatsView mineOnly={sp.get('mine') === '1'} />
}
const TicketsRoute: React.FC = () => {
  const [sp] = useSearchParams()
  return <TicketsInbox mineOnly={sp.get('mine') === '1'} />
}
const ResolutionsRoute: React.FC = () => {
  const [sp] = useSearchParams()
  return <ResolutionNotesView mineOnly={sp.get('mine') === '1'} />
}

/** Wrap a page in the shell + auth (optional role). Keeps the route list terse. */
const Page: React.FC<{ children: React.ReactNode; role?: 'supervisor' | 'admin' }> = ({ children, role }) => (
  <ProtectedRoute requiredRole={role}>
    <Layout>{children}</Layout>
  </ProtectedRoute>
)

/** Old /supervisor/:agentId → new /settings/team/:agentId */
const RedirectSupervisorAgent: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>()
  return <Navigate to={`/settings/team/${agentId}`} replace />
}

export const App: React.FC = () => {
  return (
    <AuthProvider>
      <SocketProvider>
        <GroupUnreadProvider>
        <WhatsAppStatusProvider>
        <NotificationProvider>
          <Router>
            <AppModeProvider>
            <ForwardingProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              {/* PUBLIC customer onboarding embed (Onboardings Phase 6) — no
                  WeTask login; scoped entirely by slug + token in the URL. */}
              <Route path="/crm/onboarding/:slug" element={<OnboardingEmbedPage />} />
              {/* Public customer contract page (token-gated by the API). */}
              <Route path="/contract/:slug" element={<ContractPublicPage />} />
              {/* Dev Mode — blank workspace with its own minimal shell (no support Layout). */}
              <Route path="/dev" element={<DevMode />} />

              {/* Landing → tabbed Dashboard */}
              <Route path="/" element={<Page><Navigate to="/dashboard" replace /></Page>} />
              <Route path="/dashboard" element={<Page><DashboardHome /></Page>} />
              {/* Release notes for the whole platform */}
              <Route path="/updates" element={<Page><PlatformUpdatesPage /></Page>} />
              {/* Co-browse pop-out — full-window live mirror (no Layout chrome).
                  Opened from the in-chat viewer's "Pop out" button. */}
              <Route
                path="/cobrowse-view/:chatId"
                element={
                  <ProtectedRoute>
                    <CobrowseStandalonePage />
                  </ProtectedRoute>
                }
              />

              <Route path="/profile" element={<Page><ProfilePage /></Page>} />

              {/* ─── Support ─── */}
              {/* Chats is one tabbed area (Lee, Oct 2026): Active | Missed | Tickets |
                  Resolution Notes, with a shared "My chats only" toggle. Each tab's
                  view renders inside <ChatsShell>. Active = /queue (all) or /my-chats
                  (mine); the other tabs read ?mine=1 for the filter. */}
              <Route path="/queue" element={<Page><ChatsShell><QueueWithChat /></ChatsShell></Page>} />
              <Route path="/queue/:chatId" element={<Page><ChatsShell><QueueWithChat /></ChatsShell></Page>} />
              <Route path="/my-chats" element={<Page><ChatsShell><MyChatsWithChat /></ChatsShell></Page>} />
              <Route path="/my-chats/:chatId" element={<Page><ChatsShell><MyChatsWithChat /></ChatsShell></Page>} />
              <Route path="/missed" element={<Page><ChatsShell><MissedRoute /></ChatsShell></Page>} />
              {/* History + Chat History are now tabs on the Dashboard (Lee, Oct 2026).
                  Index routes redirect to the matching Dashboard tab; the per-company
                  and per-agent detail pages stay as their own routes. */}
              <Route path="/history" element={<Navigate to="/dashboard?tab=history" replace />} />
              <Route path="/chat-history" element={<Navigate to="/dashboard?tab=monthly" replace />} />
              <Route path="/chat-history/agent/:agentId" element={<Page><AgentDetail /></Page>} />
              <Route path="/chat-history/:alias" element={<Page><ChatHistoryCompanyDetail /></Page>} />

              <Route path="/tickets" element={<Page><ChatsShell><TicketsRoute /></ChatsShell></Page>} />
              <Route path="/tickets/:id" element={<Page><ChatsShell><TicketsRoute /></ChatsShell></Page>} />
              <Route path="/groups" element={<Page><GroupsView /></Page>} />
              <Route path="/groups/:groupId" element={<Page><GroupsView /></Page>} />
              <Route path="/resolutions" element={<Page><ChatsShell><ResolutionsRoute /></ChatsShell></Page>} />
              {/* Contacts now live under Customers (Lee, Oct 2026): one area, two
                  tabs. The company detail page shows that company's contacts too. */}
              <Route path="/customers" element={<Page><CustomersShell><PaidCustomersView /></CustomersShell></Page>} />
              <Route path="/customers/:id" element={<Page><CompanyDetailView /></Page>} />
              <Route path="/contacts" element={<Page><CustomersShell><ContactsView /></CustomersShell></Page>} />
              <Route path="/visitors" element={<Page role="supervisor"><VisitorsPage /></Page>} />

              {/* AI Copilot (top-bar entry) */}
              <Route path="/ai-copilot" element={<Page><AiCopilotView /></Page>} />

              {/* ─── Consolidated Settings (gear) ─── */}
              {/* Reachable by everyone (29 Jul 2026) — sections inside are role-gated. */}
              <Route path="/settings" element={<Page><SettingsHub /></Page>} />
              <Route path="/settings/:section" element={<Page><SettingsHub /></Page>} />
              <Route path="/settings/:section/:agentId" element={<Page><SettingsHub /></Page>} />

              {/* ─── Redirects from old paths (deep links / bookmarks) ─── */}
              <Route path="/supervisor" element={<Navigate to="/settings/team" replace />} />
              <Route path="/supervisor/:agentId" element={<RedirectSupervisorAgent />} />
              <Route path="/ratings" element={<Navigate to="/settings/ratings" replace />} />
              <Route path="/notices" element={<Navigate to="/settings/notices" replace />} />
              <Route path="/integrations" element={<Navigate to="/settings/integrations" replace />} />
              <Route path="/zoho-chats" element={<Navigate to="/settings/zoho-chats" replace />} />
              <Route path="/whatsapp-archive" element={<Navigate to="/settings/whatsapp-archive" replace />} />
              <Route path="/kb-mirror" element={<Navigate to="/settings/kb-mirror" replace />} />
              <Route path="/unlinked" element={<Navigate to="/settings/unlinked" replace />} />

              {/* ─── CRM ─── */}
              <Route path="/crm" element={<Navigate to="/crm/board" replace />} />
              {/* BOARD HUB (Lee, 26 Oct 2026): Sales · Qualification · Analytics · Demos as tabs. */}
              <Route path="/crm/board" element={<Page><CrmBoardHub /></Page>} />
              <Route path="/crm/sales-board" element={<Navigate to="/crm/board?tab=sales" replace />} />
              <Route path="/crm/lifecycle" element={<Navigate to="/crm/board?tab=qualification" replace />} />
              {/* SHOWS HUB (Lee, 26 Oct 2026): Event Planner merged into Shows/Events. */}
              <Route path="/crm/opportunities" element={<Page><CrmShowsHub /></Page>} />
              <Route path="/crm/event-planner" element={<Navigate to="/crm/opportunities?tab=planner" replace />} />
              <Route path="/crm/shows/:id" element={<Page><CrmShowDetail /></Page>} />
              <Route path="/crm/lead/:id" element={<Page><CrmLeadPage /></Page>} />
              {/* LEADS HUB (Lee, 26 Oct 2026): Leads + the Billing list as a tab. */}
              <Route path="/crm/leads" element={<Page><CrmLeadsHub /></Page>} />
              <Route path="/crm/leads/import" element={<Page><CrmLeadImport /></Page>} />
              <Route path="/crm/analytics" element={<Navigate to="/crm/board?tab=analytics" replace />} />
              {/* Metrics Glossary lives in the top-bar help popup now (Lee, 26 Oct 2026). */}
              <Route path="/crm/metrics" element={<Navigate to="/crm/board" replace />} />
              <Route path="/crm/records" element={<Page><CrmRecordList /></Page>} />
              {/* COMPANIES ARE LEADS (Lee, 26 Oct 2026) — pages folded into Leads. */}
              <Route path="/crm/companies" element={<Navigate to="/crm/leads" replace />} />
              <Route path="/crm/contacts" element={<Navigate to="/crm/leads" replace />} />
              <Route path="/crm/pipeline" element={<Navigate to="/crm/board?tab=demos" replace />} />
              <Route path="/crm/billing" element={<Navigate to="/crm/leads?view=billing" replace />} />
              <Route path="/crm/reports" element={<Page><CrmReportBuilder /></Page>} />
              {/* Automation page removed from the UI — the engine + existing automations keep
                  running server-side; they're controlled by us, not from the platform. */}
              <Route path="/crm/automation" element={<Navigate to="/crm/board" replace />} />
              {/* Sequences REMOVED (Lee, 26 Oct 2026). */}
              <Route path="/crm/sequences" element={<Navigate to="/crm/email-templates" replace />} />
              <Route path="/crm/email-templates" element={<Page><CrmEmailTemplates /></Page>} />
              <Route path="/crm/customer-success" element={<Page><CrmAntiChurn /></Page>} />
              <Route path="/crm/onboardings" element={<Page><CrmOnboardings /></Page>} />
              {/* Onboardings module (Natalie's pack, Phase 1) — BEFORE :id so "todo" never matches it. */}
              <Route path="/crm/onboardings/todo" element={<Page><CrmObTodo /></Page>} />
              <Route path="/crm/onboardings/followups" element={<Page><CrmObFollowups /></Page>} />
              <Route path="/crm/onboardings/calendar" element={<Page><CrmObCalendar /></Page>} />
              <Route path="/crm/onboardings/analytics" element={<Page><CrmObAnalytics /></Page>} />
              <Route path="/crm/onboardings/co/:id" element={<Page><CrmObCompanyPage /></Page>} />
              <Route path="/crm/onboardings/:id" element={<Page><CrmOnboardingPage /></Page>} />
              <Route path="/crm/contracts" element={<Page><CrmContracts /></Page>} />
              <Route path="/crm/contracts/:leadId" element={<Page><ContractStudio /></Page>} />
              {/* MY DAY (Lee, 26 Oct 2026): Call Queue at the heart + today's schedule,
                  attention panel and to-dos. Replaces Call Queue + Task Dashboard. */}
              <Route path="/crm/my-day" element={<Page><CrmMyDay /></Page>} />
              <Route path="/crm/call-queue" element={<Navigate to="/crm/my-day" replace />} />
              <Route path="/crm/tasks" element={<Navigate to="/crm/my-day" replace />} />
              {/* CrmImport was a client-side simulation that never called an API — redirect to the real importer. */}
              <Route path="/crm/import" element={<Navigate to="/crm/leads/import" replace />} />
              <Route path="/crm/settings" element={<Navigate to="/settings/lead-settings" replace />} />

              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            </ForwardingProvider>
            </AppModeProvider>
          </Router>
        </NotificationProvider>
        </WhatsAppStatusProvider>
        </GroupUnreadProvider>
      </SocketProvider>
    </AuthProvider>
  )
}
